import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { errorMessage } from "@/lib/error-message";
import { recordAuthEvent } from "@/contexts/AuthContext";

const SITE_URL = "https://fitauraai.com";
// After verifying their email, users land on a simple confirmation page that
// tells them to open the FitAura app — it does NOT auto-route into the website.
const EMAIL_REDIRECT = `${SITE_URL}/auth/confirmed`;
const RESET_REDIRECT = `${SITE_URL}/reset-password`;

export type EmailAuthResult =
  | { ok: true; needsConfirmation?: boolean }
  | { ok: false; code: "email_not_confirmed" | "invalid_credentials" | "other"; message: string };

/**
 * Email/password auth wrapper used by the Android native EmailAuthSheet.
 * Enforces mandatory email confirmation: signInWithPassword surfaces
 * `email_not_confirmed` so the UI can prompt to resend the verification link.
 */
export function useEmailAuth() {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);

  const signUp = async (email: string, password: string): Promise<EmailAuthResult> => {
    setBusy(true);
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { emailRedirectTo: EMAIL_REDIRECT },
      });
      if (error) {
        return { ok: false, code: "other", message: error.message };
      }
      // Per Supabase: when email confirmation is required, session is null
      // and identities array is empty for already-registered emails.
      const needsConfirmation = !data.session;
      recordAuthEvent("oauth-initiated", "email-signup");
      return { ok: true, needsConfirmation };
    } catch (e) {
      return { ok: false, code: "other", message: errorMessage(e) };
    } finally {
      setBusy(false);
    }
  };

  const signIn = async (email: string, password: string): Promise<EmailAuthResult> => {
    setBusy(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        const msg = error.message || "";
        if (/email.*not.*confirm|confirm.*email/i.test(msg) || (error as { code?: string }).code === "email_not_confirmed") {
          return { ok: false, code: "email_not_confirmed", message: "Please verify your email before signing in." };
        }
        if (/invalid login|invalid_credentials/i.test(msg)) {
          return { ok: false, code: "invalid_credentials", message: "Incorrect email or password." };
        }
        return { ok: false, code: "other", message: msg };
      }
      recordAuthEvent("set-session-success", "email-password");
      return { ok: true };
    } catch (e) {
      return { ok: false, code: "other", message: errorMessage(e) };
    } finally {
      setBusy(false);
    }
  };

  const resendConfirmation = async (email: string) => {
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email,
        options: { emailRedirectTo: EMAIL_REDIRECT },
      });
      if (error) throw error;
      toast({ title: "Verification email sent", description: `Check ${email} for the link.` });
    } catch (e) {
      toast({ title: "Could not resend", description: errorMessage(e), variant: "destructive" });
    }
  };

  const sendPasswordReset = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: RESET_REDIRECT });
      if (error) throw error;
      toast({ title: "Password reset sent", description: `Check ${email} for the reset link.` });
    } catch (e) {
      toast({ title: "Could not send reset", description: errorMessage(e), variant: "destructive" });
    }
  };

  return { busy, signUp, signIn, resendConfirmation, sendPasswordReset };
}
